/**
 * @file Clinic-wide communications feed.
 * @module pages/healthcare/communications/CommunicationsPage
 */

import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Select, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";

import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import StatusTag from "../../../components/shared/StatusTag";

import {
	fetchCommunicationsFeed,
} from "../../../api/communications/communicationsFeedApi";
import type { CommunicationFeedRow } from "../../../types/communications/CommunicationFeedTypes";
import NotificationDetailModal from "./NotificationDetailModal";
import { DIRECTION_LABEL, CHANNEL_LABELS, statusLabel } from "../../../types/communications/CommunicationTypes";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

const { RangePicker } = DatePicker;

const PER_PAGE = loadUserPreferences().tableRowsPerPage;

const titleCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const CommunicationsPage: React.FC = () => {
	const [searchInput, setSearchInput] = useState<string>("");
	const [search, setSearch] = useState<string>("");
	const [type, setType] = useState<string>("");
	const [direction, setDirection] = useState<string>("");
	const [channel, setChannel] = useState<string>("");
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
	const [page, setPage] = useState<number>(1);

	const [selectedRow, setSelectedRow] = useState<CommunicationFeedRow | null>(null);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

	// One timer per keystroke, cleared by the next one, so only the pause at the end of typing
	// reaches `search` — and only that promotes into the query key and fires a request.
	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput);
			setPage(1);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchInput]);

	const filters = useMemo(
		() => ({
			search: search || undefined,
			type: type || undefined,
			direction: direction || undefined,
			channel: channel || undefined,
			date_from: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
			date_to: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
			page,
			per_page: PER_PAGE,
		}),
		[search, type, direction, channel, dateRange, page],
	);

	// The filters object IS the query key, so any filter change refetches. `keepPreviousData`
	// holds the current page on screen while the next one loads, so the table never blanks.
	const { data, isLoading } = useQuery({
		queryKey: ["communicationsFeed", filters],
		queryFn: () => fetchCommunicationsFeed(filters),
		placeholderData: keepPreviousData,
	});

	const rows = data?.data ?? [];
	const total = data?.meta?.total ?? 0;

	// Straight off the wire — never a local list. See the file header.
	const typeOptions = useMemo(
		() => (data?.types ?? []).map((t) => ({ label: t.label, value: t.value })),
		[data?.types],
	);
	const directionOptions = useMemo(
		() => (data?.directions ?? []).map((d) => ({ label: DIRECTION_LABEL[d] ?? titleCase(d), value: d })),
		[data?.directions],
	);
	const channelOptions = useMemo(
		() => (data?.channels ?? []).map((c) => ({ label: CHANNEL_LABELS[c] ?? titleCase(c), value: c })),
		[data?.channels],
	);

	const openDrawer = (row: CommunicationFeedRow) => {
		setSelectedRow(row);
		setDrawerOpen(true);
	};

	const handleReset = () => {
		setSearchInput("");
		setSearch("");
		setType("");
		setDirection("");
		setChannel("");
		setDateRange(null);
		setPage(1);
	};

	const columns: ColumnsType<CommunicationFeedRow> = [
		{
			title: "Date",
			dataIndex: "occurred_at",
			key: "occurred_at",
			width: 150,
			render: (value: string | null) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—"),
		},
		{
			title: "Type",
			dataIndex: "type_label",
			key: "type",
			render: (label: string, row) => (
				// Inline flex + gap is the design system's own idiom for laying out siblings
				// (§6) — the gap itself comes from the spacing scale rather than a bare number.
				<div style={{ display: "flex", alignItems: "center", gap: "var(--mg-sp-4)" }}>
					<span>{label}</span>
					{/* A null channel is a real value (C1 left ambiguous historic rows
					    unclassified rather than guessing) — show the gap, do not invent one. */}
					{row.channel ? (
						<StatusTag status={row.channel} size="small" />
					) : (
						<Text type="secondary" style={{ fontSize: "var(--mg-fs-xs)" }}>
							—
						</Text>
					)}
				</div>
			),
		},
		{
			title: "Direction",
			dataIndex: "direction",
			key: "direction",
			width: 110,
			render: (value: string) => (
				<StatusTag
					status={value}
					label={DIRECTION_LABEL[value] ?? value}
					color={value === "inbound" ? "var(--mg-info)" : "var(--mg-primary)"}
				/>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (value: string) => <StatusTag status={value} label={statusLabel(value)} />,
		},
		{
			title: "Patient",
			dataIndex: "patient_name",
			key: "patient_name",
			width: 180,
			render: (name: string | null, row) =>
				name ? (
					<span>{name}</span>
				) : (
					<Text type="secondary">{row.contact || "—"}</Text>
				),
		},
		{
			title: "Message",
			dataIndex: "preview",
			key: "preview",
			render: (preview: string | null) => (
				<div className="mg-truncate" style={{ maxWidth: 520 }} title={preview ?? undefined}>
					{preview || "—"}
				</div>
			),
		},
	];

	return (
		<Layout>
			<PageContainer
				data-testid="communications-page"
			>
				<PageToolbar
					filters={[
						{
							type: "custom",
							key: "search",
							render: (
								<Input
									data-testid="communications-search"
									placeholder="Search by patient name, phone or email…"
									prefix={<SearchOutlined />}
									allowClear
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									style={{ width: 280 }}
								/>
							),
						},
						{
							type: "custom",
							key: "type",
							render: (
								<Select
									data-testid="communications-type-filter"
									placeholder="Filter by type"
									options={typeOptions}
									value={type || undefined}
									onChange={(v) => {
										setType(v ?? "");
										setPage(1);
									}}
									allowClear
									showSearch
									optionFilterProp="label"
									style={{ width: 220 }}
								/>
							),
						},
						{
							type: "select",
							key: "direction",
							placeholder: "Direction",
							options: directionOptions,
							value: direction,
							onChange: (v) => {
								setDirection(v);
								setPage(1);
							},
							width: 140,
						},
						{
							type: "select",
							key: "channel",
							placeholder: "Channel",
							options: channelOptions,
							value: channel,
							onChange: (v) => {
								setChannel(v);
								setPage(1);
							},
							width: 140,
						},
						{
							type: "custom",
							key: "dateRange",
							render: (
								<RangePicker
									value={dateRange}
									onChange={(dates) => {
										setDateRange(dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : null);
										setPage(1);
									}}
									format="DD MMM YYYY"
									style={{ width: 240 }}
								/>
							),
						},
					]}
					onReset={handleReset}
				/>

				{/* DataTable takes no data-testid of its own; the anchor sits on its wrapper. */}
				<div data-testid="communications-table">
					<DataTable
						columns={columns}
						dataSource={rows}
						rowKey="id"
						loading={isLoading}
						fillHeight
						total={total}
						currentPage={page}
						pageSize={PER_PAGE}
						onPageChange={(p) => setPage(p)}
						onRowClick={openDrawer}
						emptyTitle="No communications found"
						emptyDescription="Nothing has been sent or received for the selected filters."
					/>
				</div>
			</PageContainer>

			<NotificationDetailModal
				isOpen={drawerOpen}
				notification={selectedRow}
				onClose={() => setDrawerOpen(false)}
			/>
		</Layout>
	);
};

export default CommunicationsPage;
